import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from "@angular/core";
import { Router } from "@angular/router";
import { FormBuilder, FormGroup } from "@angular/forms";

import { MemberPaths } from "src/app/constants/routes";
import { MaskPatterns, MaskType } from "src/app/constants/masks";

import { AuthService } from "src/app/services/auth.service";
import { FormValidationService } from "src/app/services/form-validation.service";
import { MemberService } from "src/app/services/member.service";
import { SessionService } from "src/app/services/storage/session.service";

import { ConfirmMember, MemberSearchResult } from "src/app/models/member-lookup";
import { ListItem } from "src/app/models/listItem";
import { AgentMemberLogin, AgentMemberLoginResponse } from "src/app/models/agent";
import { MemberLogin, MemberSession } from "src/app/models/member";
import { MatTableDataSource } from "@angular/material/table";
import { TableColumnType } from "src/app/constants/table";
import { TableColumn } from "src/app/models/table";

@Component({
    selector: 'mwp-member-confirm',
    templateUrl: './member-confirm.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MemberConfirmComponent implements OnInit {
    @Input() public member: MemberSearchResult;
    @Output() public emitBack: EventEmitter<any> = new EventEmitter();

    public maskType = MaskType;
    public confirmForm: FormGroup;
    public displayForm: boolean;
    public details: ListItem[] = [];
    public message: string;

    public memberMatTableData: MatTableDataSource<MemberSearchResult>;
    public memberColumns: TableColumn[] = [
        {
            columnName: 'SSN',
            propertyId: 'ssn',
            columnType: TableColumnType.label,
            width: '15%'
        },
        {
            columnName: 'Name',
            propertyId: 'memberName',
            columnType: TableColumnType.label,
        },
        {
            columnName: 'Company',
            propertyId: 'companyName',
            columnType: TableColumnType.label,
        },
        {
            columnName: 'Birth Date',
            propertyId: 'birthDate',
            columnType: TableColumnType.label,
            width: '15%'
        },
        {
            columnName: 'System ID',
            propertyId: 'systemNumber',
            columnType: TableColumnType.label,
            width: '10%'
        },
    ];

    constructor(
        private authService: AuthService,
        private formBuilder: FormBuilder,
        private memberService: MemberService,
        private sessionService: SessionService,
        private router: Router,
    ) {
    }

    ngOnInit(): void {
        this.displayForm = true;
        this.memberMatTableData = new MatTableDataSource<MemberSearchResult>();
        this.memberMatTableData.data = [this.member];

        this.confirmForm = this.formBuilder.group({
            ssn: [{ value: this.member.ssn, disabled: true }],
            memberName: [{ value: this.member.memberName, disabled: true }],
            companyName: [{ value: this.member.companyName, disabled: true }],
            birthDate: [{ value: this.member.birthDate, disabled: true }],
            systemNumber: [{ value: this.member.systemNumber, disabled: true }],
            email: [this.member.homeEmail, FormValidationService.validEmail(true)],
            phone: [this.member.homePhone, FormValidationService.validPhoneNumber(false)],
        });

        this.confirmForm.markAllAsTouched();

        this.memberService.getMemberConfirmation(this.member).subscribe((response: ConfirmMember) => {
            this.message = response.cannotContinueMessage;

            if (response.departmentCaption)
                this.details.push({
                    text: response.departmentCaption,
                    value: response.department,
                });

            if (response.divisionCaption)
                this.details.push({
                    text: response.divisionCaption,
                    value: response.division
                });

            if (response.locationCaption)
                this.details.push({
                    text: response.locationCaption,
                    value: response.location
                });

            this.details.push({
                text: 'Employment Status',
                value: response.employmentStatus
            });

            if (response.eeClass1Caption)
                this.details.push({
                    text: response.eeClass1Caption,
                    value: response.eeClass1
                });

            if (response.eeClass2Caption)
                this.details.push({
                    text: response.eeClass2Caption,
                    value: response.eeClass2
                });

            if (response.eeClass3Caption)
                this.details.push({
                    text: response.eeClass3Caption,
                    value: response.eeClass3
                });

            if (response.eeClass4Caption)
                this.details.push({
                    text: response.eeClass4Caption,
                    value: response.eeClass4
                });

            if (response.eeClass5Caption)
                this.details.push({
                    text: response.eeClass5Caption,
                    value: response.eeClass5
                });

            if (response.departmentCaption)
                this.details.push({
                    text: response.departmentCaption,
                    value: response.department
                });

            if (response.positionCaption)
                this.details.push({
                    text: response.positionCaption,
                    value: response.position
                });
        });
    }

    public back(): void {
        this.emitBack.emit();
    }

    public confirmMember(): void {
        const l_AgentMemberLogin = new AgentMemberLogin();
        l_AgentMemberLogin.homeEmail = this.confirmForm.get('email').value;
        l_AgentMemberLogin.homePhone = this.confirmForm.get('phone').value.replace(MaskPatterns.NON_DECIMAL_DIGITS, '');;
        l_AgentMemberLogin.systemNumber = this.member.systemNumber;
        l_AgentMemberLogin.agentID = this.sessionService.session.agent.agentID;

        const l_MemberLogin = new MemberLogin();
        l_MemberLogin.systemNumber = this.member.systemNumber;
        l_MemberLogin.agentCity = this.sessionService.session.agent.agentCity;
        l_MemberLogin.agentState = this.sessionService.session.agent.agentState;
        l_MemberLogin.adminDirectSystem = this.sessionService.session.agent.adminDirectSystem;

        this.memberService.setAgentMemberConfirmation(l_AgentMemberLogin).subscribe(() => {
            this.authService.setAgentMemberLogin(l_MemberLogin).subscribe((response: AgentMemberLoginResponse) => {
                const selectedMember: MemberSession = {
                    systemNumber: this.member.systemNumber,
                    memberName: this.member.memberName,
                    companyName: this.member.companyName,
                    isVerified: this.member.isVerified,
                    accessToken: null,
                    refreshToken: null,
                    memberSSNNoRequired: false,
                    mustResetPassword: false,
                    companyCode: null,
                    adminUserID: this.sessionService.session.agent.agentID,
                    adminUserName: this.sessionService.session.adminUserName,
                    phoneNumber: 0,
                    isEnrollmentAvailableForMember: response.isEnrollmentAvailableForMember,
                    isUSAePayEnabled: response.isUSAePayEnabled,
                    companyImagePath: response.companyImagePath,
                    dashboardTemplate: response.dashboardTemplate,
                    showDependentVerification: response.showDependentVerification,
                    showLifeEventVerification: response.showLifeEventVerification,
                    showMemberAccounting: response.showMemberAccounting,
                    requireDependentVerificationDocuments: response.requireDependentVerificationDocuments,
                    isVbo: response.isVbo,
                    dontRequireSpouseSSN: response.dontRequireSpouseSSN,
                    hasSSN: response.hasSSN,
                    showMemberWaiveDocuments: response.showMemberWaiveDocuments,
                    showWaiveDocuments: response.showWaiveDocuments,
                    enrollmentCategoryModelVBO: response.enrollmentCategoryModelVBO,
                    isOpenEnrollmentActive: false,
                    operatingMode: "",
                    id: 0,
                    divisionName: "",
                    validationMessage: "",
                    locationName: "",
                    useACAModule: response.useACAModule,
                    usePayrollModule: response.usePayrollModule,
                    useYearRound: response.useYearRound,
                    languagePreference: this.sessionService.session.isAgent ? this.sessionService.session.language : this.sessionService.session.member.languagePreference,
                    availablePTO: response.availablePTO,
                    availableSickTime: response.availableSickTime,
                    messageCenterAlerts: response.messageCenterAlerts,
                    divisionCaption: response.divisionCaption,
                    division: response.division,
                    email: response.email,
                    firstName: this.member.firstName,
                    cellPhone: null, //this.sessionService.session.member.cellPhone,
                    isCellPhoneMFAConfigured: false, //this.sessionService.session.member.isCellPhoneMFAConfigured
                    pyidMatchedMember: false // member only 
                };

                this.sessionService.session.isMember = true;
                this.sessionService.session.member = selectedMember;
                this.sessionService.setSession();
                this.router.navigateByUrl(MemberPaths.Dashboard);
            });
        });
    }
}
